import UiWidgetChainSelectorActionBar from "@/components/Ui/Widget/ChainSelector/ActionBar";
import type { Networkish } from "@/types";
import React, { Fragment, ReactNode, useMemo } from "react";

import UiWidgetChainSelectorMultiSelect from "@/components/Ui/Widget/ChainSelector/MultiSelect";
import { SupAccordion, SupAccordionDetails, SupAccordionSummary, SupText } from "@repo/core-ui";

interface UiChainSelectorProps {
  chainList: Networkish[];
  selectedChains: Networkish[];
  onClick?: (chainId: Networkish) => void;
  groupBy?: (item: Networkish) => boolean;
  groupLabel?: string | ReactNode;
  label?: string;
  onClear?: () => void;
  onSelectAll?: () => void;
  defaultExpanded?: boolean;
}

export default function UiChainSelector({
  groupBy,
  groupLabel,
  chainList: rawChainList,
  selectedChains,
  onClick,
  label = "Select chain(s)",
  defaultExpanded = false,
  onClear,
  onSelectAll,
}: UiChainSelectorProps) {
  const numberOfChains = selectedChains.length > 0 ? selectedChains.length : rawChainList.length;

  const { groupedChainList, chainList } = useMemo<{ groupedChainList: Networkish[]; chainList: Networkish[] }>(() => {
    if (!groupBy) {
      return {
        groupedChainList: [],
        chainList: rawChainList,
      };
    }

    const groupedChainList = rawChainList.filter(groupBy);

    return {
      groupedChainList,
      chainList: rawChainList.filter(item => !groupedChainList.includes(item)),
    };
  }, [rawChainList, groupBy]);

  return (
    <Fragment>
      <SupAccordion variant="primary" className="border-0.5 border-neutral-300 bg-neutral-25" defaultExpanded={defaultExpanded}>
        <SupAccordionSummary className="flex h-10 w-full items-center bg-neutral-25 px-2 hover:bg-neutral-50 [&.active]:bg-neutral-25 [&.active]:hover:bg-neutral-50">
          <SupText variant="caption" weight="bold">{`${numberOfChains} chain${numberOfChains > 1 ? "s" : ""} `}</SupText>
        </SupAccordionSummary>
        <SupAccordionDetails className="flex flex-col space-y-4 bg-neutral-25 [&.active]:bg-neutral-25">
          {groupedChainList.length > 0 && <UiWidgetChainSelectorMultiSelect label={groupLabel} selectedChains={selectedChains} chainList={groupedChainList} onClick={onClick} />}

          <UiWidgetChainSelectorMultiSelect label={label} selectedChains={selectedChains} chainList={chainList} onClick={onClick} />

          <UiWidgetChainSelectorActionBar
            isClearDisabled={selectedChains.length === 0}
            isDisabled={selectedChains.length === rawChainList.length}
            onClear={onClear}
            onSelectAll={onSelectAll}
          />
        </SupAccordionDetails>
      </SupAccordion>
    </Fragment>
  );
}
